import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Divider,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import { settingsAPI } from '../../services/adminApi';

export default function GeneralSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.get();
      setSettings(Array.isArray(res.data) ? res.data[0] : res.data);
    } catch (err) {
      setError('Errore nel caricamento delle impostazioni.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const res = await settingsAPI.update(settings);
      setSettings(res.data);
      setSuccess('Impostazioni salvate.');
    } catch (err) {
      setError('Errore nel salvataggio.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
  }

  if (!settings) {
    return <Alert severity="error">{error || 'Impostazioni non disponibili.'}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Visualizzazione
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          label="Elementi per pagina"
          type="number"
          value={settings.items_per_page}
          onChange={handleChange('items_per_page')}
          size="small"
          sx={{ width: 180 }}
          inputProps={{ min: 10, max: 200 }}
        />
        <TextField
          label="Vista predefinita"
          select
          value={settings.default_view}
          onChange={handleChange('default_view')}
          size="small"
          sx={{ width: 180 }}
        >
          <MenuItem value="grid">Griglia</MenuItem>
          <MenuItem value="table">Tabella</MenuItem>
        </TextField>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" fontWeight={600} gutterBottom>
        Upload
      </Typography>
      <TextField
        label="Dimensione max upload (MB)"
        type="number"
        value={settings.max_upload_size_mb}
        onChange={handleChange('max_upload_size_mb')}
        size="small"
        sx={{ width: 220, mb: 2 }}
        inputProps={{ min: 1, max: 5000 }}
      />
      <TextField
        label="Estensioni consentite"
        value={settings.allowed_extensions}
        onChange={handleChange('allowed_extensions')}
        size="small"
        fullWidth
        placeholder="pdf,jpg,png,docx (vuoto = tutte)"
        helperText="Separate da virgola. Lascia vuoto per consentire tutte."
        sx={{ mb: 2 }}
      />

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mt: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mt: 3 }}>{success}</Alert>}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={20} /> : <Save />}
          onClick={handleSave}
          disabled={saving}
        >
          Salva impostazioni
        </Button>
      </Box>
    </Box>
  );
}
